import type {
  ChecklistComponent,
  ChecklistResponseValue,
  ComponentOption,
  PriceBreakdown,
  PricingType,
  QuoteCalculation,
} from "@/types/components";

export class PricingService {
  private static resolveOptionValue(option: ComponentOption): string {
    if (option.option_value && option.option_value.trim() !== "") {
      return option.option_value;
    }

    return option.option_label
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
  }

  /**
   * Calculate the price for a single component based on its value
   */
  static calculateComponentPrice(
    component: ChecklistComponent,
    value: any,
    quantity?: number
  ): PriceBreakdown {
    const resolvedValue =
      value && typeof value === "object" && value !== null && "value" in value
        ? (value as { value: any }).value
        : value;
    const resolvedQuantity =
      quantity !== undefined
        ? quantity
        : value && typeof value === "object" && value !== null && "quantity" in value
          ? (value as { quantity?: number }).quantity
          : undefined;

    const breakdown: PriceBreakdown = {
      component_id: component.id || 0,
      component_label: component.label,
      base_price: component.base_price || 0,
      calculated_price: 0,
      calculation_method: component.pricing_type || "fixed",
      quantity: resolvedQuantity,
    };

    // Handle option-based components (select, checkbox, radio)
    if (["select", "checkbox", "radio"].includes(component.component_type)) {
      return PricingService.calculateOptionPrice(component, resolvedValue, breakdown);
    }

    // Handle value-based components (text, number, textarea)
    return PricingService.calculateValuePrice(component, resolvedValue, breakdown);
  }

  /**
   * Calculate price for option-based components
   */
  private static calculateOptionPrice(
    component: ChecklistComponent,
    value: any,
    breakdown: PriceBreakdown
  ): PriceBreakdown {
    if (!component.options || component.options.length === 0) {
      return breakdown;
    }

    let totalPrice = 0;
    const selectedValues = Array.isArray(value) ? value : [value];

    selectedValues.forEach((selectedValue) => {
      const option = component.options?.find((opt) => {
        const optionValue = PricingService.resolveOptionValue(opt);
        return optionValue === selectedValue;
      });
      if (option?.price_modifier) {
        switch (option.price_modifier_type) {
          case "add":
            totalPrice += option.price_modifier;
            break;
          case "multiply":
            totalPrice = breakdown.base_price * option.price_modifier;
            break;
          case "percentage":
            totalPrice = breakdown.base_price * (1 + option.price_modifier / 100);
            break;
          case "replace":
            totalPrice = option.price_modifier;
            break;
          default:
            totalPrice += option.price_modifier;
        }
      }
    });

    breakdown.calculated_price = totalPrice || breakdown.base_price;
    breakdown.details = `Selected: ${selectedValues.join(", ")}`;

    return breakdown;
  }

  /**
   * Calculate price for value-based components with quantity support
   */
  private static calculateValuePrice(
    component: ChecklistComponent,
    value: any,
    breakdown: PriceBreakdown
  ): PriceBreakdown {
    const basePrice = component.base_price || 0;
    const pricingType = component.pricing_type || "fixed";
    const qty = breakdown.quantity || Number.parseFloat(value) || component.default_quantity || 1;

    switch (pricingType) {
      case "fixed":
        breakdown.calculated_price = basePrice;
        breakdown.details = "Fixed price";
        break;

      case "per_unit":
        breakdown.quantity = qty;
        breakdown.unit_label = component.unit_label || "unit";
        breakdown.calculated_price = basePrice * qty;
        breakdown.details = `${qty} ${breakdown.unit_label} × $${basePrice.toFixed(2)}`;
        break;

      case "per_sqft":
        breakdown.quantity = qty;
        breakdown.unit_label = "sq ft";
        breakdown.calculated_price = basePrice * qty;
        breakdown.details = `${qty} sq ft × $${basePrice.toFixed(2)}/sq ft`;
        break;

      case "per_linear":
        breakdown.quantity = qty;
        breakdown.unit_label = "linear ft";
        breakdown.calculated_price = basePrice * qty;
        breakdown.details = `${qty} linear ft × $${basePrice.toFixed(2)}/linear ft`;
        break;

      case "per_character": {
        const stringValue =
          typeof value === "string"
            ? value
            : Array.isArray(value)
              ? value.join("")
              : String(value ?? "");
        const characterCount = stringValue.length;
        breakdown.quantity = characterCount;
        breakdown.unit_label = "characters";
        breakdown.calculated_price = basePrice * characterCount;
        breakdown.details = `${characterCount} characters × $${basePrice.toFixed(2)}`;
        break;
      }

      case "formula":
        // Evaluate custom formula
        breakdown.calculated_price = PricingService.evaluateFormula(
          component.formula || "",
          qty,
          basePrice
        );
        breakdown.details = `Formula: ${component.formula}`;
        break;

      case "custom": {
        const calculated = PricingService.evaluateFormula(component.formula || "", qty, basePrice);
        breakdown.quantity = qty;
        breakdown.calculated_price = calculated;
        breakdown.details = component.formula
          ? `Formula: ${component.formula}`
          : "Custom calculation";
        break;
      }

      case "none":
        breakdown.calculated_price = 0;
        breakdown.details = "No pricing";
        break;

      default:
        breakdown.calculated_price = basePrice;
        breakdown.details = "Default pricing";
    }

    return breakdown;
  }

  /**
   * Evaluate a custom formula
   * Supports variables: quantity, base_price, and basic math operations
   */
  static evaluateFormula(formula: string, quantity: number, basePrice: number): number {
    if (!formula) return basePrice;

    try {
      // Replace variables in the formula
      const evaluableFormula = formula
        .replace(/quantity/gi, quantity.toString())
        .replace(/base_price/gi, basePrice.toString())
        .replace(/price/gi, basePrice.toString())
        .replace(/qty/gi, quantity.toString());

      // Basic safety check - only allow numbers, operators, and parentheses
      if (!/^[\d\s+\-*/().]+$/.test(evaluableFormula)) {
        console.warn("Invalid formula characters detected:", formula);
        return basePrice;
      }

      // Evaluate the formula (in production, use a proper expression parser)
      // For now, using Function constructor with safety checks
      const result = new Function("return " + evaluableFormula)();

      return typeof result === "number" && !isNaN(result) ? result : basePrice;
    } catch (error) {
      console.error("Formula evaluation error:", error, formula);
      return basePrice;
    }
  }

  /**
   * Calculate total price for multiple components
   */
  static calculateTotalPrice(
    components: ChecklistComponent[],
    responses: Record<string, any>,
    quantities?: Record<string, number>
  ): {
    total: number;
    breakdowns: PriceBreakdown[];
  } {
    const breakdowns: PriceBreakdown[] = [];
    let total = 0;

    components.forEach((component) => {
      const value = responses[component.field_name];
      const quantity = quantities?.[component.field_name];

      // Skip components without values
      if (value === undefined || value === null || value === "") {
        return;
      }

      const breakdown = PricingService.calculateComponentPrice(component, value, quantity);
      breakdowns.push(breakdown);
      total += breakdown.calculated_price;
    });

    return { total, breakdowns };
  }

  /**
   * Calculate running total for a service instance
   */
  static calculateRunningTotal(calculations: QuoteCalculation[]): number {
    return calculations.reduce((total, calc) => {
      return total + (calc.calculated_price || 0);
    }, 0);
  }

  /**
   * Format price for display
   */
  static formatPrice(price: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }

  /**
   * Validate pricing configuration
   */
  static validatePricing(component: ChecklistComponent): string[] {
    const errors: string[] = [];

    // Check base price for non-option components
    if (!["select", "radio", "checkbox"].includes(component.component_type)) {
      if (component.pricing_type === "per_unit" && !component.unit_label) {
        errors.push("Unit label is required for per-unit pricing");
      }

      if (component.pricing_type === "formula" && !component.formula) {
        errors.push("Formula is required for formula-based pricing");
      }

      if (component.base_price === undefined || component.base_price < 0) {
        errors.push("Base price must be a positive number");
      }
    }

    // Check option pricing
    if (["select", "radio", "checkbox"].includes(component.component_type)) {
      if (!component.options || component.options.length === 0) {
        errors.push("Options are required for selection components");
      }
    }

    // Validate quantity constraints
    if (component.allow_quantity_input) {
      if (component.min_quantity !== undefined && component.max_quantity !== undefined) {
        if (component.min_quantity > component.max_quantity) {
          errors.push("Minimum quantity cannot be greater than maximum quantity");
        }
      }
    }

    return errors;
  }
}
